import type { TextTranslations } from './localization';

/** Host-injected global. The SDK itself is not part of this repo. */
export const COMBOS_HOST_SDK = 'combos' as const;

export interface CombosI18nRequest {
  sourceLocale: string;
  sourceTexts: string[];
}

export type CombosI18nResult =
  | { needed: false }
  | { needed: true; locale: string; translations: TextTranslations };

export interface CombosHostSdk {
  requestI18n?(request: CombosI18nRequest): Promise<CombosI18nResult>;
}

declare global {
  interface Window {
    combos?: CombosHostSdk;
  }
}

export function hasCombosRequestI18n(
  sdk: CombosHostSdk | undefined | null = typeof window === 'undefined' ? undefined : window.combos,
): sdk is CombosHostSdk & { requestI18n: NonNullable<CombosHostSdk['requestI18n']> } {
  return !!sdk && typeof sdk.requestI18n === 'function';
}

export function parseI18nResult(data: unknown): CombosI18nResult {
  if (!data || typeof data !== 'object') return { needed: false };
  const input = data as {
    needed?: unknown;
    locale?: unknown;
    translations?: unknown;
  };
  if (input.needed !== true) return { needed: false };
  if (typeof input.locale !== 'string' || !input.locale.trim()) {
    return { needed: false };
  }
  if (
    !input.translations ||
    typeof input.translations !== 'object' ||
    Array.isArray(input.translations)
  ) {
    return { needed: false };
  }
  const entries = Object.entries(input.translations);
  if (entries.some(([, value]) => typeof value !== 'string')) {
    return { needed: false };
  }
  return {
    needed: true,
    locale: input.locale.trim(),
    translations: Object.fromEntries(entries) as TextTranslations,
  };
}

/** Ask the injected host SDK. Always resolves; missing method means no translation. */
export async function requestHostI18n(
  request: CombosI18nRequest,
): Promise<CombosI18nResult> {
  if (typeof window === 'undefined' || !hasCombosRequestI18n(window.combos)) {
    return { needed: false };
  }
  try {
    return parseI18nResult(await window.combos.requestI18n(request));
  } catch {
    return { needed: false };
  }
}
